// Transitions PR-5 — the path wizards' OWN copy home (the recordPaymentCopy
// convention): step lists per exit-path family, per-path fact sheets (also
// the Ask-AI grounding context), and the load-bearing consequence copy.
// Plain language throughout — these wizards are the corrective surface for
// the handout's errors (an extension does NOT reprice the note).

import type { LoanTransitionType } from "@/api/portfolioAdmin/loanTransitionsApi";

export interface WizardStep {
  key: string;
  label: string;
}

/** The four wizard families — each family shares one step list. */
export type WizardFamily = "payoff" | "extension" | "assumption" | "engine";

export function familyFor(type: LoanTransitionType): WizardFamily {
  switch (type) {
    case "CashPayoff":
    case "BankRefinance":
    case "BmspFullPayoff":
    case "BmspThirdPartyRefinance":
      return "payoff";
    case "BmspMaturityExtension":
      return "extension";
    case "SaleWithAssumption":
    case "HardshipExit":
      return "assumption";
    case "PhoenixBackedResale":
    case "RightSizeRefinance":
      return "engine";
  }
}

export const FAMILY_STEPS: Record<WizardFamily, WizardStep[]> = {
  payoff: [
    { key: "path", label: "Exit path" },
    { key: "quote", label: "Payoff quote" },
    { key: "notice", label: "Prepayment Notice" },
    { key: "review", label: "Review & start" },
  ],
  extension: [
    { key: "path", label: "Exit path" },
    { key: "window", label: "Eligibility window" },
    { key: "terms", label: "Fee & terms" },
    { key: "review", label: "Review & start" },
  ],
  assumption: [
    { key: "path", label: "Exit path" },
    { key: "deal", label: "Incoming buyer" },
    { key: "changes", label: "What changes hands" },
    { key: "guaranties", label: "Guaranties" },
    { key: "review", label: "Review & start" },
  ],
  engine: [
    { key: "path", label: "Exit path" },
    { key: "handoff", label: "How this executes" },
    { key: "review", label: "Review & start" },
  ],
};

/** Per-path fact sheet — the wizard's review copy AND the Ask-AI grounding
 *  context ("pathFacts"). Every number here restates a platform rule, never
 *  a loan-specific figure (those come from live data). */
export const PATH_FACTS: Record<LoanTransitionType, string> = {
  CashPayoff:
    "Full cash payoff at the Yield Maintenance Amount. A quote is non-binding; " +
    "the written Prepayment Notice is irrevocable once recorded, and a missed " +
    "tender date is an automatic event of default with default-rate interest. " +
    "The payoff includes outstanding principal, accrued interest, open arrears, " +
    "the prepayment administration fee (3% unless the note says otherwise), and " +
    "the yield-maintenance premium — the remaining scheduled interest the note " +
    "would have earned.",
  BankRefinance:
    "The buyer refinances with an outside lender and pays the loan off in full " +
    "at the Yield Maintenance Amount. Mechanically identical to a cash payoff: " +
    "non-binding quote, irrevocable Prepayment Notice, automatic default on a " +
    "missed tender.",
  BmspFullPayoff:
    "The Balloon Maturity Support Program's full-payoff track: the balloon is " +
    "paid in full at maturity. Same payoff mechanics — quote, irrevocable " +
    "Prepayment Notice, tender date — anchored to the balloon maturity from the " +
    "Month-73 covenant window.",
  BmspThirdPartyRefinance:
    "The BMSP third-party refinancing track: the balloon is refinanced with an " +
    "outside lender at maturity. Same payoff mechanics — quote, irrevocable " +
    "Prepayment Notice, tender date — anchored to the balloon maturity.",
  BmspMaturityExtension:
    "A note-holder-approved extension of the balloon maturity by 1–5 years, " +
    "applied for inside the Month-73 covenant window. The extension fee is " +
    "1.00% of outstanding principal, collected when the extension executes. " +
    "The interest rate does NOT change — the rate stays at the original " +
    "10.00%; an extension never reprices the note. The BLA allows at most two " +
    "extensions.",
  SaleWithAssumption:
    "The business sells and the incoming buyer takes over the existing loan " +
    "unchanged — same rate, same schedule. The consolidated assumption fee is " +
    "3% of the loan balance at closing (a 1% processing fee plus a 2% " +
    "assumption fee), collected when the assumption executes. The exiting " +
    "buyer's guaranty continues until the tiered release conditions are met — " +
    "an assumption by itself releases no one.",
  HardshipExit:
    "An expedited assumption on documented owner incapacity (terminal " +
    "diagnosis, permanent disability, or death). The accommodation is the " +
    "expedited 30-day timeline — the standard fee set still applies: 3% of the " +
    "balance at closing (1% processing + 2% assumption). Existing guaranties " +
    "remain in effect.",
  PhoenixBackedResale:
    "A new buyer assumes the existing loan and the seller's equity gain " +
    "becomes a new Spread Note. No yield maintenance is charged. Pricing, " +
    "preview, and execution run in the dedicated Resale wizard — this " +
    "transition tracks the exit on the loan record.",
  RightSizeRefinance:
    "The business stabilized below the loan balance: the existing loan is paid " +
    "off in full (shortfall + yield maintenance + fees) and a new smaller loan " +
    "is originated at fair value. Pricing, preview, and execution run in the " +
    "dedicated Right-Size wizard — this transition tracks the exit on the loan " +
    "record.",
};

// ---- Load-bearing consequence copy ----------------------------------------

/** D-21, stated plainly — the payoff family's one irreversible moment. */
export const IRREVOCABILITY_WARNING =
  "Once the Prepayment Notice is recorded, it cannot be canceled. If the " +
  "payoff is not tendered by the committed tender date, the loan enters " +
  "default automatically and default-rate interest applies. Record the " +
  "notice only when the buyer has committed in writing.";

/** The YMP framed for a non-technical reader (quote step). */
export const YMP_FRAMING =
  "The yield-maintenance premium is the remaining scheduled interest the " +
  "note would have earned had it run to maturity — paying off early does not " +
  "reduce what the note earns.";

/** The handout-correction — surfaced verbatim in the extension wizard. */
export const EXTENSION_NO_REPRICE =
  "The rate does NOT change — your rate stays 10.00%. An extension moves the " +
  "balloon maturity; it never reprices the note.";

export const EXTENSION_FEE_FACT =
  "The extension fee is 1.00% of outstanding principal, collected through " +
  "the waterfall when the extension executes.";

export const EXTENSION_MAX_TWO =
  "The BLA allows at most two maturity extensions over the life of the loan.";

/** D-23 / D-24 — the consolidated assumption fee, stated once. */
export const ASSUMPTION_FEE_FACT =
  "The consolidated assumption fee is 3% of the loan balance at closing: a " +
  "1% processing fee plus a 2% assumption fee, collected through the " +
  "waterfall when the assumption executes.";

export const HARDSHIP_FRAMING =
  "Hardship exits run on the expedited 30-day timeline (§10.7). The " +
  "accommodation is the timeline — the standard fee set still applies.";

/** PR-4a perimeter — what executing an assumption changes, in plain words. */
export const PERIMETER_CONSEQUENCES: readonly string[] = [
  "The current borrower's portal account loses access to this loan.",
  "Their bank (ACH) authorization and any autopay enrollment are canceled — nothing carries over.",
  "The new buyer's portal account is linked (when one can be found) and they are asked to set up payment and consent themselves.",
  "The borrower roster and loan record are re-pointed to the new buyer; the operating company stays on as an obligor.",
  "A new personal guaranty is initiated for the new buyer through e-sign (§10.4).",
  "Existing guaranties remain in effect — an assumption does not release them (§10.8).",
];

/** §10.8 — the tiered release conditions are contractual text, not a
 *  platform workflow (release is unmodeled; no release fee exists — D-25). */
export const GUARANTY_RELEASE_STATIC =
  "Under §10.8, an exiting guarantor's release is tiered: it requires the " +
  "assumed loan to season under the new owner and the note holder's written " +
  "consent — it never happens automatically at closing. The platform does " +
  "not process releases today, and there is no guaranty-release fee.";

/** The engine-wizard handoff explanation (resale / right-size). */
export const ENGINE_HANDOFF: Record<
  "PhoenixBackedResale" | "RightSizeRefinance",
  { title: string; body: string; route: (loanId: string) => string; cta: string }
> = {
  PhoenixBackedResale: {
    title: "Executes in the Resale wizard",
    body:
      "Pricing, the Spread Note preview, and the closing itself run in the " +
      "dedicated Resale wizard — its forms are not repeated here. Start this " +
      "transition to track the exit on the loan record, then run the Resale " +
      "wizard when the sale is ready to close; the closing binds to this " +
      "transition automatically.",
    route: (loanId) => `/portfolio-admin/loans/${loanId}/resale`,
    cta: "Open the Resale wizard",
  },
  RightSizeRefinance: {
    title: "Executes in the Right-Size wizard",
    body:
      "The shortfall math, the payoff preview, and the new-loan origination " +
      "run in the dedicated Right-Size wizard — its forms are not repeated " +
      "here. Start this transition to track the exit on the loan record, then " +
      "run the Right-Size wizard when ready; the closing binds to this " +
      "transition automatically.",
    route: (loanId) => `/portfolio-admin/loans/${loanId}/right-size`,
    cta: "Open the Right-Size wizard",
  },
};

/** Groups + display order for the path picker. */
export const PATH_GROUPS: readonly {
  label: string;
  types: readonly LoanTransitionType[];
}[] = [
  { label: "Payoff & refinance", types: ["CashPayoff", "BankRefinance"] },
  {
    label: "Balloon maturity (BMSP)",
    types: ["BmspFullPayoff", "BmspThirdPartyRefinance", "BmspMaturityExtension"],
  },
  { label: "Sale & assumption", types: ["SaleWithAssumption", "HardshipExit"] },
  { label: "Restructure", types: ["PhoenixBackedResale", "RightSizeRefinance"] },
];
